/**
 * Registry of tools available in the DAAily Hub.
 *
 * Adding a new tool: add a slug here + a `TOOLS` entry, then build the
 * tool's routes/components and reference the slug in user provisioning.
 *
 * Slugs are stable string identifiers — they appear in user records
 * (`tools.{slug}.enabled`) and in the hub's tile rendering. NEVER rename a
 * slug after launch — write a migration that adds the new key and copies
 * data instead.
 */
export const TOOL_SLUGS = [
    'expenses', 'travel', 'invoices', 'drivers', 'creditCards',
    // HR Hub expansion (in development) — registered as `internal` so they
    // NEVER show as a public "coming soon" teaser; they appear only for users
    // explicitly provisioned for them. Kept `active: false` until each tool's
    // routes exist; flip to `active: true` per tool when ready.
    'absence', 'documents', 'orgChart', 'timeTracking', 'performanceReviews', 'commissions', 'usl', 'subscriptions', 'analytics', 'signatures',
    'projects', 'newsletter', 'operations', 'outreach', 'businessCard', 'directors',
];
/**
 * Tool registry. Order here = order on the hub.
 */
export const TOOLS = {
    expenses: {
        slug: 'expenses',
        name: 'Expense Tool',
        description: 'Submit and approve monthly expense statements.',
        active: true,
        url: '/expenses',
        icon: 'file-invoice',
    },
    travel: {
        slug: 'travel',
        name: 'Travel Booking',
        description: 'Book flights, hotels, and trains for business trips.',
        active: false,
        url: '#',
        icon: 'plane',
    },
    invoices: {
        slug: 'invoices',
        name: 'Invoice Submission',
        description: 'Submit invoices for pre-approval and forwarding to NZZ accounts payable.',
        active: true,
        url: '/invoices',
        icon: 'file-text',
    },
    drivers: {
        slug: 'drivers',
        name: 'DAAily Drivers',
        // Internal during preview — hidden from everyone until provisioned (no
        // public "coming soon" teaser). Provisioned to Michael/Falco to preview.
        description: 'Recognise colleagues for our values — nominate, celebrate monthly winners, build your trophy cabinet.',
        active: true,
        internal: true,
        url: '/drivers',
        icon: 'trophy',
    },
    creditCards: {
        slug: 'creditCards',
        name: 'Credit Card Submissions',
        description: 'Upload monthly corporate card statements and itemize them for approval.',
        active: true,
        url: '/credit-cards',
        icon: 'credit-card',
    },
    // ── HR Hub expansion (internal, in development — hidden until provisioned) ──
    absence: {
        slug: 'absence',
        name: 'Absence Management',
        description: 'Request and approve vacation and sick leave; track balances and team calendars.',
        active: true,
        internal: true,
        url: '/absence',
        icon: 'calendar',
    },
    documents: {
        slug: 'documents',
        name: 'Documents',
        description: 'Your documents — contracts, payslips, certificates shared with you by HR.',
        active: true,
        internal: true,
        url: '/documents',
        icon: 'folder',
    },
    orgChart: {
        slug: 'orgChart',
        name: 'Org Chart',
        description: 'Browse the company reporting structure, departments, and teams.',
        active: true,
        internal: true,
        url: '/org',
        icon: 'sitemap',
    },
    timeTracking: {
        slug: 'timeTracking',
        name: 'Time Tracking',
        description: 'Clock in/out, track worked hours against your target, and see your balance.',
        active: true,
        internal: true,
        url: '/time',
        icon: 'clock',
    },
    performanceReviews: {
        slug: 'performanceReviews',
        name: 'Performance Reviews',
        description: 'Complete your review and, for managers, review your team.',
        active: true,
        internal: true,
        url: '/performance-reviews',
        icon: 'chart-bar',
    },
    commissions: {
        slug: 'commissions',
        name: 'Commissions',
        description: 'Sales commission calculation, targets, and payout tracking.',
        active: true,
        internal: true,
        url: '/commissions',
        icon: 'calculator',
    },
    usl: {
        slug: 'usl',
        name: 'USL',
        description: 'Billed-revenue vs target dashboard — company summary + flex by BD.',
        active: true,
        internal: true,
        url: '/usl',
        icon: 'chart',
    },
    subscriptions: {
        slug: 'subscriptions',
        name: 'Subscriptions',
        description: 'ArchDaily subscription revenue — daily + monthly, from the Stripe export.',
        active: true,
        internal: true,
        url: '/subscriptions',
        icon: 'refresh',
    },
    analytics: {
        slug: 'analytics',
        name: 'Analytics',
        description: 'Web traffic dashboards — per-brand overview + channel mix, from the BigQuery warehouse.',
        active: true,
        internal: true,
        url: '/analytics',
        icon: 'chart',
    },
    signatures: {
        slug: 'signatures',
        name: 'Signatures',
        description: 'Send hub documents for e-signature — signed copies filed into Documents.',
        active: true,
        internal: true,
        url: '/signatures',
        icon: 'signature',
    },
    projects: {
        slug: 'projects',
        name: 'Projects',
        description: 'Product-project portfolio — track initiatives, milestones, and action items.',
        active: true,
        internal: true,
        url: '/projects',
        icon: 'layout-kanban',
    },
    operations: {
        slug: 'operations',
        name: 'DAAily Operations',
        description: 'Operational automations and monitors — crawlers, feeds, and internal ops dashboards.',
        active: true,
        internal: true,
        url: '/operations',
        icon: 'hammer',
    },
    outreach: {
        slug: 'outreach',
        name: 'Outreach',
        description: 'Templated, tracked emails sent as you — opens, clicks and Salesforce logging without Mixmax.',
        active: true,
        internal: true,
        url: '/outreach',
        icon: 'send',
    },
    businessCard: {
        slug: 'businessCard',
        name: 'Business Card',
        description: 'Your digital business card: QR code, public card page, add-to-contacts, and an exchange form for people you meet.',
        active: true,
        internal: true,
        url: '/business-card',
        icon: 'id-card',
    },
    newsletter: {
        slug: 'newsletter',
        name: 'Internal Newsletters',
        description: 'Recurring internal newsletters — contribute your section, admins assemble and send.',
        active: true,
        internal: true,
        url: '/newsletter',
        icon: 'mail',
    },
    directors: {
        slug: 'directors',
        name: "Directors' Room",
        description: "Agenda, notes, decisions and actions for the weekly directors' meeting.",
        active: true,
        internal: true,
        url: '/directors',
        icon: 'calendar',
    },
};
/**
 * Per-tool role enum — applies to all per-user tool roles
 * (`/users/{email}.tools.{slug}.role`).
 *
 * Values:
 *   `'submitter'`       — default; can create / edit / submit content for
 *                         this tool. (Equivalent to the legacy
 *                         expenses-specific `'employee'`.)
 *   `'approver'`        — can approve others' submissions in this tool
 *                         (not own). Receives approval-notification emails.
 *   `'admin'`           — can edit tool config + view all submissions.
 *                         Does NOT auto-receive approval notifications.
 *   `'approver_admin'`  — both `admin` AND `approver` rights. Receives
 *                         approval notifications.
 *
 * Cross-cutting: `User.hubRole === 'super_admin'` (defined in user.ts) is
 * an independent escape hatch — super admins bypass per-tool auth on
 * every tool, but receive notification emails only on tools where they
 * also hold an approver-flavored role.
 *
 * NOTE for backward compatibility: the legacy expenses role enum
 * (`UserRole` = `'employee' | 'admin'`) still exists in `./enums.ts` and
 * governs the legacy top-level `User.role` field. The new `ToolRole` lives
 * in `tools.expenses.role`. Both should mean the same thing for any
 * given user; `adminUpdateUser.ts` keeps them in sync on every write.
 * `'employee'` (legacy) maps to `'submitter'` (new) semantically.
 */
export const TOOL_ROLES = ['submitter', 'trusted_submitter', 'approver', 'admin', 'approver_admin', 'signatory', 'bd_viewer'];
/** Projects tool: a `trusted_submitter` is a regular member whose project
 *  requests skip the review step — projectSubmitRequest auto-accepts them
 *  (requester becomes PM, staged counterparts kept). No admin powers. */
export function projectsIsTrustedSubmitter(role) {
    return role === 'trusted_submitter';
}
/** USL tool: a `bd_viewer` sees ONLY the Flex-by-BD view for THEIR OWN rep
 *  (mapped via the year profile's target `email`). Data reaches them filtered
 *  by the `uslGetDashboard` callable; raw /uslReports //uslLines //uslForecasts
 *  doc reads are rules-DENIED for the role. 'submitter' = full viewer (today). */
export function uslIsBdViewer(role) {
    return role === 'bd_viewer';
}
/** Signatures tool: a `signatory` may VIEW their own documents + SIGN, but
 *  cannot send/request signatures. Any other enabled role can send. */
export function signaturesCanSend(role) {
    return role !== 'signatory';
}
/** Predicate: this role can approve submissions in the tool (not own).
 *  Note: super-admin bypass is checked separately via lib/roles.ts. */
export function roleCanApprove(role) {
    return role === 'approver' || role === 'admin' || role === 'approver_admin';
}
/** Predicate: this role can edit tool config + see all submissions. */
export function roleCanAdmin(role) {
    return role === 'admin' || role === 'approver_admin';
}
/** Predicate: this role auto-receives approval-notification emails. */
export function roleReceivesNotifications(role) {
    return role === 'approver' || role === 'approver_admin';
}
//# sourceMappingURL=tools.js.map